import { ChangeDetectionStrategy, Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { connectPrinter, ignorePrinter } from 'src/app/store/printer/printer.actions';
import { printerFeatureKey, PrinterState } from 'src/app/store/printer/printer.reducer';
import { PrinterService } from '@shared/services/printer.service';
import { Observable, Subscription } from 'rxjs';
import { Router, NavigationEnd } from '@angular/router';
import { filter, take } from 'rxjs/operators';
import { DynamicDialogRef } from 'primeng/dynamicdialog';

// Importar la interfaz USBDevice del servicio
interface USBDevice {
  serialNumber: string;
  productName: string;
}

@Component({
  selector: 'app-printer-config-modal',
  template: `
    <div class="printer-config-modal">
      <h2>Configurar Impresora</h2>

      <!-- Impresora ignorada -->
      <div *ngIf="isIgnored$ | async" class="p-mt-3 printer-ignored">
        <i class="pi pi-exclamation-triangle" style="font-size: 2rem; color: orange;"></i>
        <p>Configuración de impresora ignorada</p>
        <p class="ignored-description">Ha elegido ignorar la configuración de impresora. Algunas funciones que requieren impresión térmica pueden no estar disponibles.</p>
        <button pButton type="button" label="Cerrar" (click)="ref.close()" class="p-button-warning p-mt-3"></button>
      </div>

      <!-- Instrucciones iniciales -->
      <div *ngIf="!detectionStarted && !(isConnected$ | async) && !(isIgnored$ | async)" class="detection-instructions">
        <p>Para conectar la impresora térmica, siga estos pasos:</p>

        <ol>
          <li>Asegúrese de que la impresora esté encendida y conectada</li>
          <li>Pulse el botón "Seleccionar impresora"</li>
          <li>Seleccione su impresora en el diálogo del navegador</li>
        </ol>

        <!-- Mensaje especial para reconexión -->
        <div *ngIf="printerName" class="security-note p-mt-2">
          <div class="reconnection-message">
            <i class="pi pi-info-circle" style="color: #2196F3; font-size: 1.5rem;"></i>
            <div>
              <p style="font-weight: bold; margin-bottom: 0.5rem;">
                Reautorización necesaria
              </p>
              <p>Por razones de seguridad, el navegador requiere volver a autorizar el acceso a la impresora cada vez que se reinicia. Esto es normal y necesario para proteger tu privacidad.</p>
              <p>Simplemente haz clic en "Seleccionar impresora" y elige tu impresora nuevamente.</p>
            </div>
          </div>
        </div>

        <button pButton type="button" label="Seleccionar impresora" (click)="startManualSelection()" class="p-button-primary p-mt-3"></button>
      </div>

      <!-- Estado de selección manual -->
      <div *ngIf="detectionStarted && !(isConnected$ | async) && !(isIgnored$ | async)" class="detection-step">
        <div class="loading-spinner"></div>
        <p class="detection-message">Esperando selección de dispositivo...</p>
      </div>

      <!-- Estado de carga general -->
      <div *ngIf="(loading$ | async) && !detectionStarted && !(isIgnored$ | async)" class="p-mt-3">
        <div class="loading-spinner"></div>
        <p>Buscando impresora...</p>
      </div>

      <!-- Impresora conectada -->
      <div *ngIf="isConnected$ | async" class="p-mt-3 printer-connected">
        <i class="pi pi-check-circle" style="font-size: 2rem; color: green;"></i>
        <p>Impresora Conectada</p>
        <p *ngIf="printerName" class="printer-name">Nombre: {{printerName}}</p>
        <button pButton type="button" label="Cerrar" (click)="closeModal()" class="p-button-success p-mt-3"></button>
      </div>

      <!-- Error -->
      <div *ngIf="(error$ | async) && !(isIgnored$ | async)" class="p-mt-3 printer-error">
        <i class="pi pi-times-circle" style="font-size: 2rem; color: red;"></i>
        <p>Error: {{ error$ | async }}</p>
        <div class="button-row">
          <button pButton type="button" label="Reintentar" (click)="startManualSelection()" class="p-button-danger p-mt-3"></button>
          <button pButton type="button" label="Ignorar" (click)="ignorePrinterConfig()" class="p-button-warning p-mt-3 p-ml-2"></button>
        </div>
      </div>

      <!-- Tiempo agotado -->
      <div *ngIf="detectionTimeout && !(isIgnored$ | async)" class="p-mt-3 printer-error">
        <i class="pi pi-times-circle" style="font-size: 2rem; color: orange;"></i>
        <p>Tiempo de detección agotado. Por favor, inténtelo de nuevo.</p>
        <div class="button-row">
          <button pButton type="button" label="Reintentar" (click)="startManualSelection()" class="p-button-warning p-mt-3"></button>
          <button pButton type="button" label="Ignorar" (click)="ignorePrinterConfig()" class="p-button-warning p-mt-3 p-ml-2"></button>
        </div>
      </div>

      <!-- Botón de cancelar detección -->
      <div *ngIf="detectionStarted && !(isConnected$ | async) && !(isIgnored$ | async)" class="p-mt-3">
        <button pButton type="button" label="Cancelar" (click)="cancelDetection()" class="p-button-secondary p-mt-3"></button>
      </div>
    </div>
  `,
  styles: [`
    .printer-config-modal {
      padding: 20px;
      text-align: center;
    }
    .printer-connected {
      color: green;
      font-weight: bold;
    }
    .printer-error {
      color: red;
    }
    .printer-ignored {
      color: orange;
      font-weight: bold;
    }
    .ignored-description {
      font-size: 0.9rem;
      color: #666;
      margin-bottom: 15px;
    }
    .loading-spinner {
      border: 4px solid rgba(0, 0, 0, 0.1);
      width: 36px;
      height: 36px;
      border-radius: 50%;
      border-left-color: #09f;
      animation: spin 1s linear infinite;
      margin: 0 auto;
    }
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    .detection-instructions {
      text-align: left;
      margin-bottom: 20px;
    }
    .detection-instructions ol {
      margin-left: 20px;
      margin-bottom: 20px;
    }
    .reconnection-message {
      display: flex;
      padding: 12px;
      background-color: #E3F2FD;
      border-radius: 8px;
      margin: 10px 0 15px 0;
      border-left: 4px solid #2196F3;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }
    .reconnection-message i {
      margin-right: 12px;
      margin-top: 2px;
    }
    .reconnection-message p {
      margin: 0 0 8px 0;
      color: #333;
    }
    .reconnection-message p:last-child {
      margin-bottom: 0;
    }
    .detection-step {
      margin: 30px 0;
    }
    .detection-message {
      font-size: 1.2rem;
      margin: 15px 0;
    }
    .detection-timer {
      font-size: 0.9rem;
      color: #666;
    }
    .printer-name {
      font-style: italic;
      margin-top: -10px;
      margin-bottom: 15px;
    }
    .button-row {
      display: flex;
      justify-content: center;
      gap: 10px;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PrinterConfigModalComponent implements OnInit, OnDestroy {
  isConnected$: Observable<boolean>;
  isIgnored$: Observable<boolean>;
  loading$: Observable<boolean>;
  error$: Observable<any>;
  private routeSubscription: Subscription | undefined;
  private detectionSubscription: Subscription | undefined;
  private allowedRoutes = ['/panel/payments/cashclose', '/panel/payments', '/panel/dashboard'];

  // Variables para la detección
  detectionStarted = false;
  detectionTimeout = false;
  printerName: string | null = null;

  constructor(
    private store: Store<{ [printerFeatureKey]: PrinterState }>,
    private printerService: PrinterService,
    private router: Router,
    public ref: DynamicDialogRef,
    private cdr: ChangeDetectorRef
  ) {
    this.isConnected$ = this.store.pipe(select(printerFeatureKey, 'isConnected'));
    this.isIgnored$ = this.store.pipe(select(printerFeatureKey, 'isIgnored'));
    this.loading$ = this.store.pipe(select(printerFeatureKey, 'loading'));
    this.error$ = this.store.pipe(select(printerFeatureKey, 'error'));
  }

  ngOnInit(): void {
    // Verificar si ya hay una impresora conectada
    // this.isConnected$.pipe(take(1)).subscribe(isConnected => {
    //   if (isConnected) {
    //     // Obtener el nombre de la impresora desde localStorage
    //     this.printerName = localStorage.getItem('printerDevice');

    //     // Si ya está conectada, podemos cerrar el modal automáticamente después de un tiempo
    //     setTimeout(() => {
    //       this.closeModal();
    //     }, 2000);
    //   }
    // });

    // this.routeSubscription = this.router.events
    //   .pipe(filter(event => event instanceof NavigationEnd))
    //   .subscribe((event: any) => {
    //     // Solo mostrar el modal en rutas específicas si no hay impresora conectada
    //     let isConnected = false;
    //     this.isConnected$.pipe(take(1)).subscribe(connected => {
    //       isConnected = connected;
    //     });

    //     if (!isConnected && !this.allowedRoutes.includes(event.url)) {
    //       this.closeModal();
    //     }
    //   });
  }

  /**
   * Inicia la selección manual de la impresora mediante diálogo de navegador
   */
  startManualSelection(): void {
    console.log('PrinterConfigModalComponent: Iniciando selección manual de impresora');
    this.detectionStarted = true;
    this.detectionTimeout = false;
    this.cdr.detectChanges();

    // Usar la ventana de diálogo del navegador para seleccionar dispositivo
    this.printerService.requestDevice().then(success => {
      if (success) {
        console.log('PrinterConfigModalComponent: Impresora conectada manualmente');
        this.printerName = localStorage.getItem('printerDevice');
        this.detectionStarted = false;

        // Forzar la detección de cambios
        this.cdr.detectChanges();

        // Cerrar el modal después de un breve retraso
        setTimeout(() => {
          this.closeModal();
        }, 2000);
      } else {
        console.log('PrinterConfigModalComponent: No se seleccionó ninguna impresora válida');
        this.detectionTimeout = true;
        this.detectionStarted = false;
        this.cdr.detectChanges();
      }
    }).catch(error => {
      console.error('PrinterConfigModalComponent: Error al iniciar selección manual:', error);
      this.detectionTimeout = true;
      this.detectionStarted = false;
      this.cdr.detectChanges();
    });
  }

  /**
   * Inicia el proceso de selección de impresora (método de compatibilidad)
   */
  startDetection(): void {
    // Por compatibilidad, usar el método manual
    this.startManualSelection();
  }

  /**
   * Cancela el proceso de detección
   */
  cancelDetection(): void {
    this.printerService.cancelPrinterDetection();
    this.detectionStarted = false;
  }

  /**
   * Ignora la configuración de impresora
   */
  ignorePrinterConfig(): void {
    console.log('PrinterConfigModalComponent: Ignorando configuración de impresora');
    // Despachar la acción para ignorar la impresora
    this.store.dispatch(ignorePrinter());

    // Cerrar el modal después de un breve retraso
    setTimeout(() => {
      if (this.ref) {
        this.ref.close();
      }
    }, 1000);
  }

  closeModal(): void {
    // Verificar si hay una impresora conectada o se ha ignorado la configuración
    let canClose = false;

    // Suscribirse a los observables isConnected e isIgnored
    this.isConnected$.pipe(take(1)).subscribe(isConnected => {
      canClose = isConnected;
    });

    this.isIgnored$.pipe(take(1)).subscribe(isIgnored => {
      if (isIgnored) {
        canClose = true;
      }
    });

    // Permitir cerrar si hay una impresora conectada o se ha ignorado la configuración
    if (this.ref && canClose) {
      this.ref.close();
    } else if (this.ref) {
      console.log('No se puede cerrar el modal sin configurar una impresora o ignorarla');
      // Opcionalmente, mostrar un mensaje al usuario
      alert('Debe configurar una impresora o ignorar la configuración para continuar');
    }
  }

  ngOnDestroy(): void {
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }

    if (this.detectionSubscription) {
      this.detectionSubscription.unsubscribe();
    }

    // Asegurarse de cancelar cualquier detección en curso
    if (this.detectionStarted) {
      this.printerService.cancelPrinterDetection();
    }
  }
}
